// Share one register with other faculty members. Only the faculty member who
// took it can open this. Each person gets one of two levels of access:
//   View   open it
//   Edit   also change its marks, date and slot
// Deleting and sharing stay with the creator. People it is shared with see it
// in their registers list, marked "Shared".
import { useEffect, useState } from "react"
import { AlertCircle, Loader2, Share2, X } from "lucide-react"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { environment } from "@/env/env.prod"
import { apiHeaders } from "@/Auth/api-headers."
import { TeacherCombobox } from "../ClassRooms/TeacherCombobox"

type Access = "view" | "edit"
// facultyEmail / facultyPhoto come from newer servers (null on older ones)
type Share = { facultyEid: string; facultyName: string | null; sharedAt: number | null; access?: Access; facultyEmail?: string | null; facultyPhoto?: string | null }

const initialsFor = (name: string) => name.split(" ").map(n => n[ 0 ]).join("").substring(0, 2).toUpperCase()

const ACCESS_LABEL: Record<Access, string> = { view: "Can view", edit: "Can edit" }

const MESSAGES: Record<string, string> = {
    CANNOT_SHARE_WITH_SELF: "This register is already yours.",
    FACULTY_NOT_FOUND: "That faculty member wasn't found in this institute.",
    TOO_MANY_SHARES: "This register is shared with the most people allowed (50).",
    SHARE_NOT_FOUND: "That share was already removed.",
    INVALID_ACCESS: "Pick view or edit.",
}

async function call(path: string, method: "GET" | "POST", payload: Record<string, string>) {
    const url = method === "GET"
        ? `${environment.BASE_URL}/attendance/${path}?${new URLSearchParams(payload)}`
        : `${environment.BASE_URL}/attendance/${path}`
    const res = await fetch(url, { method, headers: apiHeaders() as HeadersInit, body: method === "POST" ? JSON.stringify(payload) : undefined })
    const json = await res.json()
    if (!json.success) throw new Error(MESSAGES[ json.message ] || json.message || "Request failed")
    return json.shares as Share[]
}

// View / Edit picker (a small select, the same in both places it's used).
function AccessSelect({ value, onChange, disabled }: { value: Access; onChange: (a: Access) => void; disabled?: boolean }) {
    return (
        <Select value={value} onValueChange={(v) => onChange(v as Access)} disabled={disabled}>
            <SelectTrigger className="h-8 w-[110px] text-xs"><SelectValue>{ACCESS_LABEL[ value ]}</SelectValue></SelectTrigger>
            <SelectContent>
                <SelectItem value="view">Can view</SelectItem>
                <SelectItem value="edit">Can edit</SelectItem>
            </SelectContent>
        </Select>
    )
}

export function ShareRegisterDialog({ sessionId, title, onClose }: { sessionId: string | null; title: string; onClose: () => void }) {
    const [ shares, setShares ] = useState<Share[]>([])
    const [ loading, setLoading ] = useState(false)
    const [ busy, setBusy ] = useState<string | null>(null)
    const [ error, setError ] = useState<string | null>(null)
    const [ newAccess, setNewAccess ] = useState<Access>("view")

    useEffect(() => {
        if (!sessionId) return
        setShares([]); setError(null); setLoading(true); setNewAccess("view")
        call("listRegisterShares", "GET", { sessionId })
            .then(setShares)
            .catch(e => setError(e.message))
            .finally(() => setLoading(false))
    }, [ sessionId ])

    if (!sessionId) return null

    const run = async (key: string, fn: () => Promise<Share[]>) => {
        setBusy(key); setError(null)
        try { setShares(await fn()) } catch (e: any) { setError(e.message) } finally { setBusy(null) }
    }
    // sharing again with the same person changes their access
    const share = (facultyEid: string, access: Access, key: string) =>
        run(key, () => call("shareRegister", "POST", { sessionId, facultyEid, access }))

    return (
        <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose() }}>
            <DialogContent className="sm:max-w-xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Share2 className="h-5 w-5 text-primary" /> Share register</DialogTitle>
                    <DialogDescription>{title}. "Can view" opens it; "Can edit" also changes its marks, date and slot. Only you can delete or share it.</DialogDescription>
                </DialogHeader>

                {error && (
                    <p className="flex items-center gap-2 rounded-md border border-red-200 bg-red-50 p-2 text-sm text-red-700">
                        <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                    </p>
                )}

                <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">Add a faculty member</p>
                    <div className="flex items-center gap-2">
                        <div className="min-w-0 flex-1">
                            <TeacherCombobox
                                value=""
                                placeholder="Search faculty by name…"
                                onSelect={(t) => share(t.employee_id, newAccess, `add:${t.employee_id}`)}
                            />
                        </div>
                        <AccessSelect value={newAccess} onChange={setNewAccess} disabled={!!busy} />
                    </div>
                    {busy?.startsWith("add:") && <p className="flex items-center gap-1.5 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sharing…</p>}
                </div>

                <div className="space-y-1.5">
                    <p className="text-xs font-medium text-muted-foreground">Shared with ({shares.length})</p>
                    {loading ? (
                        <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                    ) : shares.length === 0 ? (
                        <p className="rounded-md border border-dashed p-4 text-center text-sm text-muted-foreground">Only you can see this register.</p>
                    ) : (
                        <ul className="max-h-72 divide-y overflow-y-auto rounded-md border">
                            {shares.map(s => (
                                <li key={s.facultyEid} className="flex items-center gap-3 px-3 py-2">
                                    <Avatar className="h-8 w-8 shrink-0 border border-border/40">
                                        <AvatarImage src={s.facultyPhoto ?? ""} />
                                        <AvatarFallback className="bg-muted text-[10px] font-bold uppercase">{initialsFor(s.facultyName || "?")}</AvatarFallback>
                                    </Avatar>
                                    <span className="min-w-0 flex-1">
                                        <span className="block truncate text-sm font-medium">{s.facultyName || "Faculty member"}</span>
                                        {s.facultyEmail && <span className="block truncate text-xs text-muted-foreground">{s.facultyEmail}</span>}
                                    </span>
                                    {busy === `acc:${s.facultyEid}`
                                        ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                                        : <AccessSelect value={s.access === "edit" ? "edit" : "view"} disabled={!!busy}
                                            onChange={(a) => share(s.facultyEid, a, `acc:${s.facultyEid}`)} />}
                                    <Button size="sm" variant="ghost" className="gap-1 text-muted-foreground hover:text-red-600" disabled={!!busy}
                                        onClick={() => run(`rm:${s.facultyEid}`, () => call("unshareRegister", "POST", { sessionId, facultyEid: s.facultyEid }))}>
                                        {busy === `rm:${s.facultyEid}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />} Remove
                                    </Button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    )
}
